import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Box,
  Heading,
  Text,
  HStack,
  VStack,
  Badge,
  IconButton,
  Drawer,
  Card,
  Editable,
  Spinner,
  Grid,
  NativeSelectRoot,
  NativeSelectField,
  MenuRoot,
  MenuTrigger,
  MenuPositioner,
  MenuContent,
  MenuItem,
} from '@chakra-ui/react'
import {
  ChevronUp,
  ChevronDown,
  X,
  Trash2,
  User,
  Mail,
  Phone,
  Pencil,
  Check,
  Clock,
  CalendarClock,
  MoreVertical,
  ArrowRightLeft,
} from 'lucide-react'
import { useUnit, useDeleteUnit, useUpdateUnit, useUnitOwners } from '../hooks/useUnits'
import { useAuth } from '@/features/auth/hooks/useAuth'
import { LoadingState } from '@/shared/components/LoadingState'
import { ErrorState } from '@/shared/components/ErrorState'
import { UnitMutationsManagementModal } from './UnitMutationsManagementModal'

interface UnitDetailDrawerProps {
  unitId: string | null
  isOpen: boolean
  onClose: () => void
  onNavigatePrevious?: () => void
  onNavigateNext?: () => void
  hasPrevious?: boolean
  hasNext?: boolean
}

// Composant interne pour les champs éditables
interface EditableFieldProps {
  label: string
  value: string | number | null | undefined
  fieldName: string
  placeholder?: string
  type?: 'text' | 'number'
  onUpdate: (fieldName: string, value: string | number) => Promise<void>
}

function EditableField({
  label,
  value,
  fieldName,
  placeholder = 'Non renseigné',
  type = 'text',
  onUpdate,
}: EditableFieldProps) {
  const [isLoading, setIsLoading] = useState(false)
  const [currentValue, setCurrentValue] = useState(value?.toString() || '')

  useEffect(() => {
    setCurrentValue(value?.toString() || '')
  }, [value])

  const handleSubmit = async (details: { value: string }) => {
    const newValue = details.value
    if (newValue !== (value?.toString() || '')) {
      setIsLoading(true)
      try {
        const finalValue = type === 'number' && newValue ? Number(newValue) : newValue
        await onUpdate(fieldName, finalValue)
      } catch (error) {
        console.error('Error updating field:', error)
      } finally {
        setIsLoading(false)
      }
    }
  }

  return (
    <Box>
      <Text fontSize="sm" color="gray.600" mb={1}>
        {label}
      </Text>
      <Editable.Root
        value={currentValue}
        onValueChange={(e) => setCurrentValue(e.value)}
        onValueCommit={handleSubmit}
        placeholder={placeholder}
        disabled={isLoading}
      >
        <Editable.Preview
          fontWeight="medium"
          color={currentValue ? 'inherit' : 'gray.400'}
          px={2}
          py={1}
          borderRadius="md"
          display="flex"
          alignItems="center"
          gap={2}
          _hover={{ bg: 'gray.50' }}
        />
        <Editable.Input px={2} py={1} type={type} />
        <Editable.Control display="flex" gap={1} alignItems="center">
          {isLoading ? (
            <Spinner size="sm" />
          ) : (
            <>
              <Editable.EditTrigger asChild>
                <IconButton variant="ghost" size="xs" aria-label="Edit">
                  <Pencil size={14} />
                </IconButton>
              </Editable.EditTrigger>
              <Editable.SubmitTrigger asChild>
                <IconButton variant="ghost" size="xs" colorPalette="green" aria-label="Submit">
                  <Check size={14} />
                </IconButton>
              </Editable.SubmitTrigger>
              <Editable.CancelTrigger asChild>
                <IconButton variant="ghost" size="xs" colorPalette="red" aria-label="Cancel">
                  <X size={14} />
                </IconButton>
              </Editable.CancelTrigger>
            </>
          )}
        </Editable.Control>
      </Editable.Root>
    </Box>
  )
}

// Composant pour le sélecteur de type éditable
interface EditableSelectFieldProps {
  label: string
  value: string | null
  fieldName: string
  options: { value: string; label: string }[]
  onUpdate: (fieldName: string, value: string) => Promise<void>
}

function EditableSelectField({
  label,
  value,
  fieldName,
  options,
  onUpdate,
}: EditableSelectFieldProps) {
  const [isLoading, setIsLoading] = useState(false)

  const handleChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newValue = e.target.value
    if (newValue !== (value || '')) {
      setIsLoading(true)
      try {
        await onUpdate(fieldName, newValue)
      } catch (error) {
        console.error('Error updating field:', error)
      } finally {
        setIsLoading(false)
      }
    }
  }

  return (
    <Box>
      <Text fontSize="sm" color="gray.600" mb={1}>
        {label}
      </Text>
      <NativeSelectRoot>
        <NativeSelectField
          value={value || ''}
          onChange={handleChange}
          opacity={isLoading ? 0.6 : 1}
          pointerEvents={isLoading ? 'none' : 'auto'}
          fontWeight="medium"
        >
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </NativeSelectField>
      </NativeSelectRoot>
      {isLoading && <Spinner size="sm" mt={1} />}
    </Box>
  )
}

export function UnitDetailDrawer({
  unitId,
  isOpen,
  onClose,
  onNavigatePrevious,
  onNavigateNext,
  hasPrevious,
  hasNext,
}: UnitDetailDrawerProps) {
  const { t } = useTranslation()
  const { data: unit, isLoading, error } = useUnit(unitId ?? undefined)
  const { data: ownerships } = useUnitOwners(unitId ?? undefined)
  const { data: auth } = useAuth()
  const deleteUnit = useDeleteUnit()
  const updateUnit = useUpdateUnit()
  const [mutationsManagementOpen, setMutationsManagementOpen] = useState(false)

  // Check if user can delete this unit
  // Only ADMIN and MANAGER can delete units
  const canDelete = () => {
    if (!unit || !auth) return false

    // ADMIN can delete all units
    if (auth.isAdmin) return true

    // MANAGER can delete units in their managed residences
    return auth.isManager(unit.residence_id)
  }

  // Options pour le type de lot
  const unitTypeOptions = [
    { value: 'RESIDENTIAL', label: t('units.unit_types.RESIDENTIAL') },
    { value: 'PARKING', label: t('units.unit_types.PARKING') },
    { value: 'CELLAR', label: t('units.unit_types.CELLAR') },
    { value: 'COMMERCIAL', label: t('units.unit_types.COMMERCIAL') },
  ]

  const handleFieldUpdate = async (fieldName: string, value: string | number) => {
    if (!unitId) return

    try {
      await updateUnit.mutateAsync({
        id: unitId,
        [fieldName]: value || null,
      })
    } catch (error) {
      console.error('Error updating unit:', error)
      alert(t('common.error'))
    }
  }

  const handleDelete = async () => {
    if (!unitId) return
    if (!window.confirm(t('units.delete_confirm'))) return

    try {
      await deleteUnit.mutateAsync(unitId)
      onClose()
    } catch (error) {
      console.error('Error deleting unit:', error)
      alert(t('common.error'))
    }
  }

  const getUnitTypeColor = (type: string | null) => {
    switch (type) {
      case 'RESIDENTIAL':
        return 'blue'
      case 'PARKING':
        return 'purple'
      case 'CELLAR':
        return 'gray'
      case 'COMMERCIAL':
        return 'green'
      default:
        return 'gray'
    }
  }

  // Removed unused function getFloorDisplay

  const handleInteractOutside = (e: any) => {
    const target = e.detail.originalEvent.target as HTMLElement

    // Chercher la ligne du tableau cliquée (uniquement les lignes de données, pas le header)
    const tableRow = target.closest('[data-unit-row]') as HTMLElement
    const unitCard = target.closest('[data-unit-card]') as HTMLElement

    if (tableRow) {
      // Récupérer l'ID du unit depuis la ligne
      // La ligne a un onClick qui sera déclenché, donc on ne fait rien ici
      // Le handleUnitClick parent sera appelé automatiquement
      return
    }

    if (unitCard) {
      // Même chose pour les cards
      return
    }

    // Clic vraiment en dehors (ni tableau ni cards) → Fermer le drawer
    onClose()
  }

  return (
    <Drawer.Root
      open={isOpen}
      placement="end"
      size="lg"
      modal={false}
      onInteractOutside={handleInteractOutside}
    >
      <Drawer.Positioner pointerEvents="none">
        <Drawer.Content maxW="800px" pointerEvents="auto">
          <Drawer.Header borderBottomWidth="1px" py={4} overflow="visible">
          <Box display="flex" alignItems="center" w="full" gap={3}>
            {/* Navigation arrows - Left */}
            <Box display="flex" gap={1} flexShrink={0}>
              <IconButton
                variant="ghost"
                size="sm"
                onClick={onNavigatePrevious}
                disabled={!hasPrevious}
                aria-label={t('common.previous')}
                title={t('common.previous')}
              >
                <ChevronUp size={18} />
              </IconButton>
              <IconButton
                variant="ghost"
                size="sm"
                onClick={onNavigateNext}
                disabled={!hasNext}
                aria-label={t('common.next')}
                title={t('common.next')}
              >
                <ChevronDown size={18} />
              </IconButton>
            </Box>

            {/* Title - Center */}
            <Box flex={1} minW={0}>
              {unit && (
                <>
                  <Text fontSize="xs" color="gray.500" fontWeight="medium" lineHeight="1.3">
                    {unit.residences?.name || 'Résidence'}
                  </Text>
                  <Heading
                    size="md"
                    lineHeight="1.3"
                    overflow="hidden"
                    textOverflow="ellipsis"
                    whiteSpace="nowrap"
                  >
                    Lot {unit.unit_number}
                  </Heading>
                  {unit.unit_type && (
                    <HStack gap={1.5} mt={1}>
                      <Badge colorPalette={getUnitTypeColor(unit.unit_type)} size="sm">
                        {t(`units.unit_types.${unit.unit_type}`)}
                      </Badge>
                    </HStack>
                  )}
                </>
              )}
              {!unit && <Heading size="md">{t('units.details')}</Heading>}
            </Box>

            {/* Actions menu and close - Right */}
            <Box display="flex" gap={2} flexShrink={0}>
              {unit && (
                <Box position="relative">
                  <MenuRoot>
                    <MenuTrigger asChild>
                      <IconButton
                        variant="ghost"
                        size="sm"
                        aria-label={t('common.actions')}
                      >
                        <MoreVertical size={18} />
                      </IconButton>
                    </MenuTrigger>
                    <MenuPositioner>
                      <MenuContent minW="180px" zIndex={10}>
                        <MenuItem
                          value="mutations"
                          onClick={() => setMutationsManagementOpen(true)}
                        >
                          <HStack gap={2}>
                            <ArrowRightLeft size={16} />
                            <Text>Gérer les mutations</Text>
                          </HStack>
                        </MenuItem>
                        {canDelete() && (
                          <MenuItem
                            value="delete"
                            onClick={handleDelete}
                            color="red.600"
                            _hover={{ bg: 'red.50' }}
                          >
                            <HStack gap={2}>
                              <Trash2 size={16} />
                              <Text>{t('common.delete')}</Text>
                            </HStack>
                          </MenuItem>
                        )}
                      </MenuContent>
                    </MenuPositioner>
                  </MenuRoot>
                </Box>
              )}
              <IconButton variant="ghost" size="sm" onClick={onClose} aria-label={t('common.cancel')}>
                <X size={18} />
              </IconButton>
            </Box>
          </Box>
        </Drawer.Header>

        <Drawer.Body p={0}>
          {isLoading && <Box p={6}><LoadingState message={t('common.loading')} /></Box>}
          {error && <Box p={6}><ErrorState message={t('common.error')} /></Box>}
          {unit && (
            <>
              {/* Timestamps */}
              <HStack
                gap={3}
                px={6}
                pt={4}
                pb={3}
                bg="gray.50"
                borderBottomWidth="1px"
                borderColor="gray.200"
                flexWrap="wrap"
              >
                <HStack gap={2} flex={1} minW="200px">
                  <Box color="blue.500">
                    <CalendarClock size={16} />
                  </Box>
                  <VStack align="flex-start" gap={0}>
                    <Text fontSize="xs" color="gray.600" fontWeight="medium">
                      {t('common.created_on')}
                    </Text>
                    <Text fontSize="sm" fontWeight="semibold" color="gray.800">
                      {new Date(unit.created_at ?? '').toLocaleDateString('fr-FR', {
                        day: 'numeric',
                        month: 'long',
                        year: 'numeric',
                      })}
                    </Text>
                  </VStack>
                </HStack>

                {unit.updated_at && unit.updated_at !== unit.created_at && (
                  <HStack gap={2} flex={1} minW="200px">
                    <Box color="green.500">
                      <Clock size={16} />
                    </Box>
                    <VStack align="flex-start" gap={0}>
                      <Text fontSize="xs" color="gray.600" fontWeight="medium">
                        {t('common.last_updated')}
                      </Text>
                      <Text fontSize="sm" fontWeight="semibold" color="gray.800">
                        {new Date(unit.updated_at).toLocaleDateString('fr-FR', {
                          day: 'numeric',
                          month: 'long',
                          year: 'numeric',
                        })}
                      </Text>
                    </VStack>
                  </HStack>
                )}
              </HStack>

              <Box px={6} py={6}>
                <VStack align="stretch" gap={6}>
              {/* Cards Identification et Propriétaires */}
              <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={4}>
                {/* Card Identification */}
                <Card.Root variant="outline">
                  <Card.Header>
                    <Heading size="sm">Identification</Heading>
                  </Card.Header>
                  <Card.Body>
                    <VStack align="stretch" gap={4}>
                      <EditableField
                        label={t('units.fields.unit_number')}
                        value={unit.unit_number}
                        fieldName="unit_number"
                        onUpdate={handleFieldUpdate}
                      />
                      <EditableSelectField
                        label={t('units.fields.unit_type')}
                        value={unit.unit_type}
                        fieldName="unit_type"
                        options={unitTypeOptions}
                        onUpdate={handleFieldUpdate}
                      />
                    </VStack>
                  </Card.Body>
                </Card.Root>

                {/* Card Propriétaires actuels */}
                <Card.Root variant="outline">
                  <Card.Header>
                    <Heading size="sm">Propriétaire(s) actuel(s)</Heading>
                  </Card.Header>
                  <Card.Body>
                    {ownerships && ownerships.length > 0 ? (
                      <VStack align="stretch" gap={4}>
                        {ownerships.map((ownership, index) => (
                          <VStack
                            key={ownership.id}
                            align="stretch"
                            gap={2}
                            pb={index < ownerships.length - 1 ? 4 : 0}
                            borderBottomWidth={index < ownerships.length - 1 ? '1px' : 0}
                            borderColor="gray.200"
                          >
                            <HStack gap={2}>
                              <User size={16} color="var(--chakra-colors-brand-500)" />
                              <Text fontWeight="semibold" fontSize="sm">
                                {ownership.owners?.first_name} {ownership.owners?.last_name}
                              </Text>
                              {!(ownership.share_numerator === 1 && ownership.share_denominator === 1) && (
                                <Badge colorPalette="brand" size="sm" ml="auto">
                                  Quote-part : {ownership.share_numerator}/{ownership.share_denominator}
                                </Badge>
                              )}
                            </HStack>
                            {ownership.owners?.email && (
                              <HStack gap={2} fontSize="xs" color="gray.600">
                                <Mail size={12} />
                                <Text>{ownership.owners.email}</Text>
                              </HStack>
                            )}
                            {ownership.owners?.phone && (
                              <HStack gap={2} fontSize="xs" color="gray.600">
                                <Phone size={12} />
                                <Text>{ownership.owners.phone}</Text>
                              </HStack>
                            )}
                          </VStack>
                        ))}
                      </VStack>
                    ) : (
                      <Text color="gray.500" fontSize="sm">
                        Aucun propriétaire enregistré
                      </Text>
                    )}
                  </Card.Body>
                </Card.Root>
              </Grid>

              {/* General Info */}
              <Card.Root variant="outline">
                <Card.Header>
                  <Heading size="sm">{t('units.sections.general_info')}</Heading>
                </Card.Header>
                <Card.Body>
                  <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={4}>
                    <EditableField
                      label={t('units.fields.floor')}
                      value={unit.floor}
                      fieldName="floor"
                      type="number"
                      placeholder="Non renseigné"
                      onUpdate={handleFieldUpdate}
                    />
                    <EditableField
                      label={t('units.fields.stairwell')}
                      value={unit.stairwell}
                      fieldName="stairwell"
                      placeholder="Non renseigné"
                      onUpdate={handleFieldUpdate}
                    />
                    <EditableField
                      label={t('units.fields.area_sqm')}
                      value={unit.area_sqm}
                      fieldName="area_sqm"
                      type="number"
                      placeholder="Non renseigné"
                      onUpdate={handleFieldUpdate}
                    />
                  </Grid>
                </Card.Body>
              </Card.Root>

              {/* Shares */}
              <Card.Root variant="outline">
                <Card.Header>
                  <Heading size="sm">{t('units.sections.shares')}</Heading>
                </Card.Header>
                <Card.Body>
                  <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={4}>
                    <EditableField
                      label={t('units.fields.general_shares')}
                      value={unit.general_shares}
                      fieldName="general_shares"
                      type="number"
                      placeholder="0"
                      onUpdate={handleFieldUpdate}
                    />
                    <EditableField
                      label={t('units.fields.work_fund_share')}
                      value={unit.work_fund_share}
                      fieldName="work_fund_share"
                      type="number"
                      placeholder="0"
                      onUpdate={handleFieldUpdate}
                    />
                  </Grid>
                </Card.Body>
              </Card.Root>

              {/* Notes */}
              <Card.Root variant="outline">
                <Card.Header>
                  <Heading size="sm">{t('units.fields.notes')}</Heading>
                </Card.Header>
                <Card.Body>
                  <EditableField
                    label=""
                    value={unit.notes}
                    fieldName="notes"
                    placeholder="Aucune note"
                    onUpdate={handleFieldUpdate}
                  />
                </Card.Body>
              </Card.Root>
                </VStack>
              </Box>
            </>
          )}
        </Drawer.Body>
        </Drawer.Content>
      </Drawer.Positioner>

      {/* Modale de gestion des mutations */}
      {unitId && unit && ownerships && (
        <UnitMutationsManagementModal
          isOpen={mutationsManagementOpen}
          onClose={() => setMutationsManagementOpen(false)}
          unitId={unitId}
          unitNumber={unit.unit_number}
          currentOwners={ownerships.map((ownership) => ({
            ownerId: ownership.owner_id,
            ownerName: `${ownership.owners?.first_name || ''} ${ownership.owners?.last_name || ''}`.trim() || 'Sans nom',
            ownershipId: ownership.id,
            shareNumerator: ownership.share_numerator,
            shareDenominator: ownership.share_denominator,
            ownershipType: 'full' as const,
          }))}
        />
      )}
    </Drawer.Root>
  )
}
